import { useState, useEffect } from 'react';
import { api } from '../App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { FolderOpen, Plus, Upload, FileText, Download, Trash2, Loader2, Calendar, User, ChevronRight, ArrowLeft, UserCircle, FolderPlus, Mail, Send } from 'lucide-react';

const DOCUMENT_TYPES = [
  { value: 'bol', label: 'Bill of Lading (BOL)' },
  { value: 'pod', label: 'Proof of Delivery (POD)' },
  { value: 'rate_con', label: 'Rate Confirmation' },
  { value: 'invoice', label: 'Invoice' },
  { value: 'lumper', label: 'Lumper Receipt' },
  { value: 'scale_ticket', label: 'Scale Ticket' },
  { value: 'other', label: 'Other' }
];

export default function DispatcherFolders({ user }) {
  const [folders, setFolders] = useState([]);
  const [dispatchers, setDispatchers] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [folderDocuments, setFolderDocuments] = useState([]);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  
  // Driver sub-folders
  const [driverSubfolders, setDriverSubfolders] = useState([]);
  const [selectedSubfolder, setSelectedSubfolder] = useState(null);
  const [subfolderDocs, setSubfolderDocs] = useState([]);
  const [showDriverFolderDialog, setShowDriverFolderDialog] = useState(false);
  const [newDriverName, setNewDriverName] = useState('');
  const [newDriverId, setNewDriverId] = useState('');
  
  // Create folder form
  const [newFolderData, setNewFolderData] = useState({
    dispatcher_id: '', dispatcher_name: '', dispatcher_pin: '', description: ''
  });

  // Email Factoring dialog state
  const [factoringTarget, setFactoringTarget] = useState(null); // folder being emailed
  const [factoringForm, setFactoringForm] = useState({ recipient_email: '', subject: '', body: '' });
  const [sendingFactoring, setSendingFactoring] = useState(false);

  const openFactoringDialog = (folder, e) => {
    if (e) e.stopPropagation();
    if (!folder.document_count) {
      toast.error('This folder has no documents to send');
      return;
    }
    setFactoringTarget(folder);
    setFactoringForm({
      recipient_email: '',
      subject: `Paperwork Batch — ${folder.dispatcher_name} (PIN ${folder.dispatcher_pin}, ${folder.year})`,
      body: `Hi,\n\nAttached are the paperwork documents for dispatcher ${folder.dispatcher_name} (PIN ${folder.dispatcher_pin}) for the ${folder.year} fiscal year.\n\nPlease process at your earliest convenience.\n\nThanks,\n${user.name}`,
    });
  };

  const sendFactoringEmail = async () => {
    if (!factoringTarget) return;
    if (!factoringForm.recipient_email.trim()) {
      toast.error('Recipient email required');
      return;
    }
    setSendingFactoring(true);
    try {
      const res = await api.post(`/folders/${factoringTarget.id}/email-factoring`, factoringForm);
      toast.success(`Sent ${res.data.attached_count} doc(s) to ${res.data.recipient}`);
      setFactoringTarget(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Send failed');
    } finally {
      setSendingFactoring(false);
    }
  };
  
  // Upload form
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadType, setUploadType] = useState('bol');
  const [uploadLoadNumber, setUploadLoadNumber] = useState('');
  const [uploadNotes, setUploadNotes] = useState('');
  const [uploading, setUploading] = useState(false);

  const isBrokerOrAdmin = user?.role === 'super_admin' || user?.role === 'broker' || user?.role === 'broker_manager';

  useEffect(() => {
    loadFolders();
    if (isBrokerOrAdmin) loadDispatchers();
    api.get('/drivers').then(res => setDrivers(res.data || [])).catch(() => {});
  }, [selectedYear]);

  const loadFolders = () => {
    setLoading(true);
    api.get(`/folders?year=${selectedYear}`)
      .then(res => setFolders(res.data))
      .catch(() => toast.error('Failed to load folders'))
      .finally(() => setLoading(false));
  };

  const loadDispatchers = () => {
    api.get('/folders/dispatchers').then(res => setDispatchers(res.data)).catch(() => {});
  };

  const openFolder = (folder) => {
    setSelectedFolder(folder);
    setSelectedSubfolder(null);
    // Load both documents and driver sub-folders
    api.get(`/folders/${folder.id}/documents`).then(res => setFolderDocuments(res.data.documents)).catch(() => {});
    api.get(`/folders/${folder.id}/driver-folders`).then(res => setDriverSubfolders(res.data || [])).catch(() => setDriverSubfolders([]));
  };

  const openSubfolder = (sf) => {
    setSelectedSubfolder(sf);
    api.get(`/driver-folders/${sf.id}/documents`).then(res => setSubfolderDocs(res.data.documents)).catch(() => setSubfolderDocs([]));
  };

  const createFolder = () => {
    if (!newFolderData.dispatcher_pin) { toast.error('Select a dispatcher'); return; }
    api.post('/folders', { ...newFolderData, year: selectedYear })
      .then(() => { toast.success('Agent folder created!'); setShowCreateDialog(false); setNewFolderData({ dispatcher_id: '', dispatcher_name: '', dispatcher_pin: '', description: '' }); loadFolders(); })
      .catch(err => toast.error(err.response?.data?.detail || 'Failed'));
  };

  const createDriverFolder = () => {
    if (!newDriverName) { toast.error('Enter driver name'); return; }
    api.post(`/folders/${selectedFolder.id}/driver-folders`, { driver_name: newDriverName, driver_id: newDriverId })
      .then(res => {
        if (res.data.already_existed) toast.info('Driver folder already exists');
        else toast.success(`Folder created for ${newDriverName}`);
        setShowDriverFolderDialog(false);
        setNewDriverName('');
        setNewDriverId('');
        api.get(`/folders/${selectedFolder.id}/driver-folders`).then(r => setDriverSubfolders(r.data || []));
      })
      .catch(err => toast.error(err.response?.data?.detail || 'Failed'));
  };

  const deleteFolder = (folderId) => {
    if (!window.confirm('Delete this folder and ALL documents? This cannot be undone.')) return;
    api.delete(`/folders/${folderId}`)
      .then(() => { toast.success('Folder deleted'); setSelectedFolder(null); loadFolders(); })
      .catch(err => toast.error(err.response?.data?.detail || 'Failed'));
  };

  const deleteSubfolder = (sfId) => {
    if (!window.confirm('Delete this driver folder and all its documents?')) return;
    api.delete(`/driver-folders/${sfId}`)
      .then(() => { toast.success('Driver folder deleted'); setSelectedSubfolder(null); api.get(`/folders/${selectedFolder.id}/driver-folders`).then(r => setDriverSubfolders(r.data || [])); })
      .catch(err => toast.error(err.response?.data?.detail || 'Failed'));
  };

  const handleUpload = async () => {
    if (!uploadFile) { toast.error('Select a file'); return; }
    setUploading(true);
    const formData = new FormData();
    formData.append('file', uploadFile);
    formData.append('document_type', uploadType);
    formData.append('load_number', uploadLoadNumber);
    formData.append('notes', uploadNotes);

    try {
      const endpoint = selectedSubfolder
        ? `/driver-folders/${selectedSubfolder.id}/upload`
        : `/folders/${selectedFolder.id}/upload`;
      await api.post(endpoint, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Document uploaded!');
      setShowUploadDialog(false);
      setUploadFile(null); setUploadLoadNumber(''); setUploadNotes('');
      if (selectedSubfolder) openSubfolder(selectedSubfolder);
      else openFolder(selectedFolder);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally { setUploading(false); }
  };

  const downloadDoc = (doc) => {
    api.get(`/folders/documents/${doc.id}/download`, { responseType: 'blob' })
      .then(res => { const a = document.createElement('a'); a.href = URL.createObjectURL(res.data); a.download = doc.original_filename; a.click(); })
      .catch(() => toast.error('Download failed'));
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-slate-400" /></div>;

  // ===== VIEWING DRIVER SUB-FOLDER DOCUMENTS =====
  if (selectedFolder && selectedSubfolder) {
    return (
      <div className="space-y-4" data-testid="driver-subfolder-view">
        <div className="flex items-center gap-2 text-sm text-slate-500">
          <button onClick={() => { setSelectedFolder(null); setSelectedSubfolder(null); }} className="hover:text-blue-600">Paperwork</button>
          <ChevronRight className="w-4 h-4" />
          <button onClick={() => setSelectedSubfolder(null)} className="hover:text-blue-600">{selectedFolder.dispatcher_name} ({selectedFolder.dispatcher_pin})</button>
          <ChevronRight className="w-4 h-4" />
          <span className="font-medium text-slate-900">{selectedSubfolder.driver_name}</span>
        </div>

        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-2xl font-bold text-slate-900">{selectedSubfolder.driver_name}'s Folder</h2>
            <p className="text-sm text-slate-500">Agent: {selectedSubfolder.agent_name} (PIN: {selectedSubfolder.agent_pin}) {selectedSubfolder.folder_number && <span className="text-blue-600 font-mono">| File #{selectedSubfolder.folder_number}</span>}</p>
          </div>
          <div className="flex gap-2">
            <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
              <DialogTrigger asChild>
                <Button size="sm" data-testid="upload-to-subfolder-btn"><Upload className="w-4 h-4 mr-1" /> Upload</Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>Upload to {selectedSubfolder.driver_name}'s Folder</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <div><Label>Document Type</Label>
                    <Select value={uploadType} onValueChange={setUploadType}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>{DOCUMENT_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                    </Select></div>
                  <div><Label>Load Number (optional)</Label><Input value={uploadLoadNumber} onChange={e => setUploadLoadNumber(e.target.value)} /></div>
                  <div><Label>File</Label><Input type="file" onChange={e => setUploadFile(e.target.files[0])} data-testid="subfolder-file-input" /></div>
                  <div><Label>Notes</Label><Input value={uploadNotes} onChange={e => setUploadNotes(e.target.value)} /></div>
                  <Button onClick={handleUpload} disabled={uploading} className="w-full">{uploading ? 'Uploading...' : 'Upload'}</Button>
                </div>
              </DialogContent>
            </Dialog>
            <Button variant="outline" size="sm" onClick={() => setSelectedSubfolder(null)} data-testid="back-to-agent-folder"><ArrowLeft className="w-4 h-4 mr-1" /> Back</Button>
          </div>
        </div>

        {/* Driver Info Card */}
        {selectedSubfolder.driver_info && (
          <Card data-testid="driver-info-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <User className="w-5 h-5 text-blue-600" />
                Driver Information
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                {selectedSubfolder.driver_info.phone && <div><p className="text-xs text-slate-500">Phone</p><p className="font-medium">{selectedSubfolder.driver_info.phone}</p></div>}
                {selectedSubfolder.driver_info.email && <div><p className="text-xs text-slate-500">Email</p><p className="font-medium">{selectedSubfolder.driver_info.email}</p></div>}
                {selectedSubfolder.driver_info.license_number && <div><p className="text-xs text-slate-500">License #</p><p className="font-medium">{selectedSubfolder.driver_info.license_number} ({selectedSubfolder.driver_info.license_state || ''})</p></div>}
                {selectedSubfolder.driver_info.license_expiration && <div><p className="text-xs text-slate-500">License Exp.</p><p className="font-medium">{selectedSubfolder.driver_info.license_expiration}</p></div>}
                {selectedSubfolder.driver_info.cdl_class && <div><p className="text-xs text-slate-500">CDL Class</p><p className="font-medium">{selectedSubfolder.driver_info.cdl_class}</p></div>}
                {selectedSubfolder.driver_info.medical_card_expiration && <div><p className="text-xs text-slate-500">Medical Card Exp.</p><p className="font-medium">{selectedSubfolder.driver_info.medical_card_expiration}</p></div>}
                {selectedSubfolder.driver_info.dob && <div><p className="text-xs text-slate-500">DOB</p><p className="font-medium">{selectedSubfolder.driver_info.dob}</p></div>}
                {selectedSubfolder.driver_info.truck_number && <div><p className="text-xs text-slate-500">Truck #</p><p className="font-medium">{selectedSubfolder.driver_info.truck_number}</p></div>}
                {selectedSubfolder.driver_info.trailer_number && <div><p className="text-xs text-slate-500">Trailer #</p><p className="font-medium">{selectedSubfolder.driver_info.trailer_number}</p></div>}
                {selectedSubfolder.driver_info.address && selectedSubfolder.driver_info.address.trim() !== ',' && <div className="col-span-2"><p className="text-xs text-slate-500">Address</p><p className="font-medium">{selectedSubfolder.driver_info.address}</p></div>}
              </div>
              {(selectedSubfolder.driver_info.emergency_contact_name || selectedSubfolder.driver_info.ceo_name) && (
                <div className="grid grid-cols-2 gap-3 mt-3 pt-3 border-t text-sm">
                  {selectedSubfolder.driver_info.emergency_contact_name && (
                    <div><p className="text-xs text-slate-500">Emergency Contact</p><p className="font-medium">{selectedSubfolder.driver_info.emergency_contact_name} — {selectedSubfolder.driver_info.emergency_contact_phone || 'N/A'}{selectedSubfolder.driver_info.emergency_contact_relation ? ` (${selectedSubfolder.driver_info.emergency_contact_relation})` : ''}</p></div>
                  )}
                  {selectedSubfolder.driver_info.ceo_name && (
                    <div><p className="text-xs text-slate-500">CEO / Owner</p><p className="font-medium">{selectedSubfolder.driver_info.ceo_name} — {selectedSubfolder.driver_info.ceo_phone || 'N/A'}{selectedSubfolder.driver_info.ceo_email ? ` | ${selectedSubfolder.driver_info.ceo_email}` : ''}</p></div>
                  )}
                  {selectedSubfolder.driver_info.compliance_notes && (
                    <div className="col-span-2"><p className="text-xs text-slate-500">Compliance Notes</p><p className="font-medium">{selectedSubfolder.driver_info.compliance_notes}</p></div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {subfolderDocs.length === 0 ? (
          <Card><CardContent className="py-8 text-center text-slate-500">No documents yet. Upload BOL, POD, or other paperwork.</CardContent></Card>
        ) : (
          <div className="space-y-2">
            {subfolderDocs.map(doc => (
              <Card key={doc.id} className="hover:shadow-sm" data-testid={`subfolder-doc-${doc.id}`}>
                <CardContent className="py-3 flex items-center gap-4">
                  <div className={`px-2 py-1 rounded text-xs font-bold ${
                    doc.document_type === 'bol' ? 'bg-blue-100 text-blue-700' :
                    doc.document_type === 'pod' ? 'bg-green-100 text-green-700' :
                    doc.document_type === 'signed_pod' ? 'bg-emerald-100 text-emerald-700' :
                    'bg-slate-100 text-slate-700'}`}>
                    {doc.document_type?.toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm truncate">{doc.original_filename}</p>
                    <p className="text-xs text-slate-400">{doc.uploaded_by_name} | {new Date(doc.created_at).toLocaleString()}</p>
                    {doc.load_number && <p className="text-xs text-blue-600">Load: {doc.load_number}</p>}
                  </div>
                  <Button size="sm" variant="ghost" onClick={() => downloadDoc(doc)}><Download className="w-4 h-4" /></Button>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    );
  }

  // ===== VIEWING AGENT FOLDER (with driver sub-folders) =====
  if (selectedFolder) {
    return (
      <div className="space-y-4" data-testid="agent-folder-view">
        <div className="flex items-center gap-2 text-sm text-slate-500">
          <button onClick={() => setSelectedFolder(null)} className="hover:text-blue-600">Paperwork</button>
          <ChevronRight className="w-4 h-4" />
          <span className="font-medium text-slate-900">{selectedFolder.dispatcher_name} ({selectedFolder.dispatcher_pin})</span>
        </div>

        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-2xl font-bold text-slate-900">{selectedFolder.dispatcher_name}'s Folder</h2>
            <p className="text-sm text-slate-500">PIN: {selectedFolder.dispatcher_pin} | Year: {selectedFolder.year}</p>
          </div>
          <div className="flex gap-2">
            <Dialog open={showDriverFolderDialog} onOpenChange={setShowDriverFolderDialog}>
              <DialogTrigger asChild>
                <Button size="sm" data-testid="create-driver-folder-btn"><FolderPlus className="w-4 h-4 mr-1" /> Add Driver Folder</Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>Create Driver Folder</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <p className="text-sm text-slate-600">Create a folder for a driver under {selectedFolder.dispatcher_name}. Documents uploaded by this driver will automatically flow here.</p>
                  <div>
                    <Label>Driver</Label>
                    <Select onValueChange={v => {
                      const d = drivers.find(dr => dr.id === v);
                      if (d) { setNewDriverName(d.name); setNewDriverId(d.id); }
                    }}>
                      <SelectTrigger><SelectValue placeholder="Select driver or type below" /></SelectTrigger>
                      <SelectContent>{drivers.map(d => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div><Label>Or enter driver name</Label><Input placeholder="Driver name" value={newDriverName} onChange={e => setNewDriverName(e.target.value)} data-testid="driver-folder-name-input" /></div>
                  <Button onClick={createDriverFolder} className="w-full" data-testid="confirm-driver-folder-btn">Create Driver Folder</Button>
                </div>
              </DialogContent>
            </Dialog>
            <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
              <DialogTrigger asChild>
                <Button size="sm" variant="outline" data-testid="upload-to-folder-btn"><Upload className="w-4 h-4 mr-1" /> Upload</Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>Upload to {selectedFolder.dispatcher_name}'s Folder</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <div><Label>Document Type</Label>
                    <Select value={uploadType} onValueChange={setUploadType}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>{DOCUMENT_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                    </Select></div>
                  <div><Label>Load Number (optional)</Label><Input value={uploadLoadNumber} onChange={e => setUploadLoadNumber(e.target.value)} /></div>
                  <div><Label>File</Label><Input type="file" onChange={e => setUploadFile(e.target.files[0])} data-testid="folder-file-input" /></div>
                  <div><Label>Notes</Label><Input value={uploadNotes} onChange={e => setUploadNotes(e.target.value)} /></div>
                  <Button onClick={handleUpload} disabled={uploading} className="w-full">{uploading ? 'Uploading...' : 'Upload'}</Button>
                </div>
              </DialogContent>
            </Dialog>
            {isBrokerOrAdmin && <Button variant="destructive" size="sm" onClick={() => deleteFolder(selectedFolder.id)} data-testid="delete-folder-btn"><Trash2 className="w-4 h-4" /></Button>}
            <Button variant="outline" size="sm" onClick={() => setSelectedFolder(null)}><ArrowLeft className="w-4 h-4 mr-1" /> Back</Button>
          </div>
        </div>

        {/* Driver Sub-Folders */}
        {driverSubfolders.length > 0 && (
          <div>
            <h3 className="text-sm font-semibold text-slate-500 uppercase mb-2">Driver Folders ({driverSubfolders.length})</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {driverSubfolders.map(sf => (
                <Card key={sf.id} className="cursor-pointer hover:shadow-md hover:border-blue-300 transition-all" onClick={() => openSubfolder(sf)} data-testid={`driver-folder-${sf.id}`}>
                  <CardContent className="pt-4 pb-3 flex items-center gap-3">
                    <div className="bg-indigo-100 p-2 rounded-lg"><UserCircle className="w-5 h-5 text-indigo-600" /></div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-sm truncate">{sf.driver_name}</p>
                      <p className="text-xs text-slate-400">{sf.folder_number ? <span className="text-blue-600 font-mono">{sf.folder_number}</span> : ''} {sf.document_count || 0} docs</p>
                    </div>
                    {isBrokerOrAdmin && (
                      <button onClick={(e) => { e.stopPropagation(); deleteSubfolder(sf.id); }} className="text-slate-400 hover:text-red-600"><Trash2 className="w-3.5 h-3.5" /></button>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Direct Documents (not in a sub-folder) */}
        <div>
          <h3 className="text-sm font-semibold text-slate-500 uppercase mb-2">Documents ({folderDocuments.length})</h3>
          {folderDocuments.length === 0 ? (
            <Card><CardContent className="py-6 text-center text-slate-500 text-sm">No documents yet.</CardContent></Card>
          ) : (
            <div className="space-y-2">
              {folderDocuments.map(doc => (
                <Card key={doc.id} data-testid={`folder-doc-${doc.id}`}>
                  <CardContent className="py-3 flex items-center gap-4">
                    <div className={`px-2 py-1 rounded text-xs font-bold ${
                      doc.document_type === 'bol' ? 'bg-blue-100 text-blue-700' :
                      doc.document_type === 'pod' ? 'bg-green-100 text-green-700' :
                      'bg-slate-100 text-slate-700'}`}>
                      {doc.document_type?.toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm truncate">{doc.original_filename}</p>
                      <p className="text-xs text-slate-400">{doc.uploaded_by_name} | {new Date(doc.created_at).toLocaleString()}</p>
                      {doc.load_number && <p className="text-xs text-blue-600">Load: {doc.load_number}</p>}
                    </div>
                    <div className="flex gap-1">
                      <Button size="sm" variant="ghost" onClick={() => downloadDoc(doc)}><Download className="w-4 h-4" /></Button>
                      {isBrokerOrAdmin && <Button size="sm" variant="ghost" className="text-red-600" onClick={() => {
                        api.delete(`/folders/documents/${doc.id}`).then(() => { toast.success('Deleted'); openFolder(selectedFolder); }).catch(() => toast.error('Failed'));
                      }}><Trash2 className="w-3.5 h-3.5" /></Button>}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  // ===== FOLDER LIST VIEW =====
  return (
    <div className="space-y-6" data-testid="paperwork-page">
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Paperwork</h1>
          <p className="text-slate-600">Agent folders with driver sub-folders. {isBrokerOrAdmin ? 'Create folders for your agents.' : 'Click your folder to manage driver documents.'}</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-slate-500" />
            <Select value={String(selectedYear)} onValueChange={v => setSelectedYear(parseInt(v))}>
              <SelectTrigger className="w-24"><SelectValue /></SelectTrigger>
              <SelectContent>
                {[2024, 2025, 2026].map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {isBrokerOrAdmin && (
            <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
              <DialogTrigger asChild>
                <Button data-testid="create-folder-btn"><Plus className="w-4 h-4 mr-1" /> Create Agent Folder</Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>Create Agent Folder</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <p className="text-sm text-slate-600">Select an agent to create their master paperwork folder.</p>
                  <div>
                    <Label>Select Agent</Label>
                    <Select onValueChange={v => {
                      const d = dispatchers.find(a => a.id === v);
                      if (d) setNewFolderData({ ...newFolderData, dispatcher_id: d.id, dispatcher_name: d.name, dispatcher_pin: d.pin_code });
                    }}>
                      <SelectTrigger data-testid="select-agent"><SelectValue placeholder="Choose agent" /></SelectTrigger>
                      <SelectContent>{dispatchers.map(d => <SelectItem key={d.id} value={d.id}>{d.name} (PIN: {d.pin_code})</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div><Label>Description (optional)</Label><Input value={newFolderData.description} onChange={e => setNewFolderData({...newFolderData, description: e.target.value})} placeholder="e.g., Reefer loads team" /></div>
                  <Button onClick={createFolder} className="w-full" data-testid="confirm-create-folder-btn">Create Folder</Button>
                </div>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>

      {folders.length === 0 ? (
        <Card><CardContent className="py-12 text-center">
          <FolderOpen className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <p className="text-lg font-medium text-slate-900">{isBrokerOrAdmin ? 'No agent folders yet' : 'No folders assigned to you'}</p>
          <p className="text-sm text-slate-500">{isBrokerOrAdmin ? 'Create folders for your agents to organize their driver paperwork.' : 'Ask your broker to create a folder for you.'}</p>
        </CardContent></Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {folders.map(folder => (
            <Card key={folder.id} className="cursor-pointer hover:shadow-md hover:border-blue-300 transition-all" onClick={() => openFolder(folder)} data-testid={`agent-folder-${folder.id}`}>
              <CardContent className="pt-5 pb-4">
                <div className="flex items-start gap-3">
                  <div className="bg-blue-100 p-3 rounded-lg"><FolderOpen className="w-6 h-6 text-blue-600" /></div>
                  <div className="flex-1">
                    <p className="font-bold text-lg text-slate-900">{folder.dispatcher_name}</p>
                    <p className="text-sm text-blue-600 font-mono">PIN: {folder.dispatcher_pin}</p>
                    {folder.description && <p className="text-xs text-slate-500 mt-1">{folder.description}</p>}
                    <div className="flex items-center gap-3 mt-2 text-xs text-slate-400">
                      <span><FileText className="w-3 h-3 inline mr-1" />{folder.document_count || 0} docs</span>
                      <span>{folder.year}</span>
                    </div>
                    {isBrokerOrAdmin && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="mt-2 h-7 text-[11px] border-emerald-300 bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
                        onClick={(e) => openFactoringDialog(folder, e)}
                        disabled={!folder.document_count}
                        data-testid={`email-factoring-${folder.id}`}
                      >
                        <Mail className="w-3 h-3 mr-1" />
                        Email Factoring
                      </Button>
                    )}
                  </div>
                  <ChevronRight className="w-5 h-5 text-slate-400 mt-1" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Email Factoring Dialog */}
      <Dialog open={!!factoringTarget} onOpenChange={(o) => !o && setFactoringTarget(null)}>
        <DialogContent className="max-w-lg" data-testid="email-factoring-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5 text-emerald-600" />
              Email Factoring · {factoringTarget?.dispatcher_name}
            </DialogTitle>
          </DialogHeader>
          <p className="text-xs text-slate-500 -mt-2">
            Auto-attaches all <strong>{factoringTarget?.document_count || 0}</strong> document(s) in this folder.
          </p>
          <div className="space-y-3 py-2">
            <div>
              <Label className="text-xs">Factoring Company Email</Label>
              <Input
                type="email"
                value={factoringForm.recipient_email}
                onChange={e => setFactoringForm(f => ({ ...f, recipient_email: e.target.value }))}
                placeholder="factoring@company.com"
                data-testid="factoring-recipient-input"
              />
            </div>
            <div>
              <Label className="text-xs">Subject</Label>
              <Input
                value={factoringForm.subject}
                onChange={e => setFactoringForm(f => ({ ...f, subject: e.target.value }))}
                data-testid="factoring-subject-input"
              />
            </div>
            <div>
              <Label className="text-xs">Message</Label>
              <textarea
                value={factoringForm.body}
                onChange={e => setFactoringForm(f => ({ ...f, body: e.target.value }))}
                rows={6}
                className="w-full text-sm border border-slate-200 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-200"
                data-testid="factoring-body-input"
              />
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setFactoringTarget(null)} disabled={sendingFactoring}>Cancel</Button>
            <Button
              onClick={sendFactoringEmail}
              disabled={sendingFactoring || !factoringForm.recipient_email.trim()}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="send-factoring-btn"
            >
              {sendingFactoring ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Sending…</> : <><Send className="w-4 h-4 mr-2" />Send</>}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
